// Dashboard Selectors
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { DashboardState } from './dashboard.state';

export const selectDashboardState = createFeatureSelector<DashboardState>('dashboard');

// Data selectors
export const selectRows = createSelector(
  selectDashboardState,
  (state) => state.rows
);

export const selectFilteredRows = createSelector(
  selectDashboardState,
  (state) => state.filteredRows
);

export const selectVisibleRows = createSelector(
  selectDashboardState,
  (state) => state.filteredRows.length > 0 || state.searchQuery || 
    Object.values(state.filters).some(v => v) 
    ? state.filteredRows 
    : state.rows
);

// Pagination selectors
export const selectTotal = createSelector(
  selectDashboardState,
  (state) => state.total
);

export const selectPageSize = createSelector(
  selectDashboardState,
  (state) => state.pageSize
);

export const selectCurrentPage = createSelector(
  selectDashboardState,
  (state) => state.currentPage
);

export const selectHasMore = createSelector(
  selectDashboardState,
  (state) => state.hasMore
);

export const selectNextCursor = createSelector(
  selectDashboardState,
  (state) => state.nextCursor
);

export const selectTotalPages = createSelector(
  selectTotal,
  selectPageSize,
  (total, pageSize) => Math.max(1, Math.ceil(total / pageSize))
);

export const selectCanGoNext = createSelector(
  selectCurrentPage,
  selectTotalPages,
  selectHasMore,
  (currentPage, totalPages, hasMore) => currentPage < totalPages || hasMore
);

export const selectCanGoPrevious = createSelector(
  selectCurrentPage,
  (currentPage) => currentPage > 1
);

// Search selectors
export const selectSearchQuery = createSelector(
  selectDashboardState,
  (state) => state.searchQuery
);

export const selectAppliedQuery = createSelector(
  selectDashboardState,
  (state) => state.appliedQuery
);

// Filter selectors
export const selectFilters = createSelector(
  selectDashboardState,
  (state) => state.filters
);

export const selectHubOptions = createSelector(
  selectDashboardState,
  (state) => state.hubOptions
);

export const selectEnvironmentOptions = createSelector(
  selectDashboardState,
  (state) => state.environmentOptions
);

export const selectDeviceTypeOptions = createSelector(
  selectDashboardState,
  (state) => state.deviceTypeOptions
);

export const selectIsFiltered = createSelector(
  selectFilters,
  selectSearchQuery,
  (filters, searchQuery) => {
    const hasFilters = Object.values(filters).some(v => v !== '');
    const hasSearch = searchQuery.trim().length > 0;
    return hasFilters || hasSearch;
  }
);

// Sort selectors
export const selectSortKey = createSelector(
  selectDashboardState,
  (state) => state.sortKey
);

export const selectSortDirection = createSelector(
  selectDashboardState,
  (state) => state.sortDirection
);

// Loading selectors
export const selectLoading = createSelector(
  selectDashboardState,
  (state) => state.loading
);

export const selectRefreshing = createSelector(
  selectDashboardState,
  (state) => state.refreshing
);

export const selectError = createSelector(
  selectDashboardState,
  (state) => state.error
);

// Device type map
export const selectDeviceTypeMap = createSelector(
  selectDashboardState,
  (state) => state.deviceTypeMap
);

// Display stats
export const selectDisplayTotal = createSelector(
  selectFilteredRows,
  selectTotal,
  selectIsFiltered,
  (filteredRows, total, isFiltered) => isFiltered ? filteredRows.length : total
);

export const selectRangeStart = createSelector(
  selectCurrentPage,
  selectPageSize,
  selectDisplayTotal,
  (currentPage, pageSize, total) => total === 0 ? 0 : (currentPage - 1) * pageSize + 1
);

export const selectRangeEnd = createSelector(
  selectCurrentPage,
  selectPageSize,
  selectDisplayTotal,
  (currentPage, pageSize, total) => Math.min(currentPage * pageSize, total)
);
